import { useHistoryCustomBack } from 'weblite-router'
import { ExpandMoreIcon, WCheckIcon } from '@weblite/icons'
import {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react'
import useAutocomplete, {
  AutocompleteGetTagProps,
} from '@mui/base/useAutocomplete'
import { unstable_useForkRef as useForkRef } from '@mui/utils'
import { styled } from '@mui/material/styles'
import { autocompleteClasses } from '@mui/material/Autocomplete'
import { Typography, alpha, IconButton, FormHelperText } from '@mui/material'
import Popper from '@mui/base/Popper'
import { nothing } from '$func/utils'
import { CircularLoading } from '$common/loading/circular-loading'
import { t } from '$i18n'

export function sortArrayByArray(arr: any[], sortingArr: any[]) {
  return arr.sort((a, b) => sortingArr.indexOf(a) - sortingArr.indexOf(b))
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  max-width: 100%;
  &[data-is-full-width='true'] {
    width: 100%;
  }
  margin-top: 8px;
  margin-bottom: 16px;
`

const Label = styled('label')`
  ${({ theme }) => ({
    ...theme.typography.subtitle3,
  })}
  color:${({ theme }) => theme.palette.neutral600};
  margin-right: 8px;
  margin-bottom: 8px;
  width: fit-content;
  display: flex;
  cursor: pointer;
`

const InputWrapper = styled('div')`
  width: 100%;
  outline: 1px solid ${({ theme }) => theme.palette.neutral300};
  border-radius: 12px;
  padding: 8px 8px 8px 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  overflow: hidden;
  min-height: 48px;
  position: relative;
  transition: border-color 0.3s ease;

  &:hover {
    outline-color: ${({ theme }) => theme.palette.primary500};
  }

  &.focused {
    outline-color: ${({ theme }) => theme.palette.primary500};
  }

  &.error {
    outline-color: ${({ theme }) => theme.palette.error300};
  }

  & > .overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    cursor: pointer;
  }
  & > .expand-icon {
    color: ${({ theme }) => theme.palette.neutral500};
    position: absolute;
    top: 24px;
    transform: translateY(-50%);
    left: 6px;
    [data-is-open='true'] {
      transform: rotate(180deg);
    }
  }
  user-select: none;
  & input {
    ${({ theme }) => ({ ...theme.typography.body2 })};
    color: ${({ theme }) => theme.palette.neutral900};
    &::placeholder {
      color: ${({ theme }) => theme.palette.neutral400};
    }
    height: 20px;
    padding: 4px 6px;
    box-sizing: border-box;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    background: transparent;
    outline: 0;
    &.hidden {
      height: 0;
      padding: 0;
    }
    &[data-focusable='false'] {
      cursor: pointer;
      &::selection {
        background: transparent;
      }
    }
  }

  &[data-disabled='true'] {
    border: 1px solid ${({ theme }) => theme.palette.neutral200};
  }
`

interface TagProps extends Partial<ReturnType<AutocompleteGetTagProps>> {
  label: string
}

function Tag(props: TagProps) {
  const { label, onDelete, ...other } = props
  return (
    <div {...other} onClick={nothing}>
      <Typography variant="body2">{label}</Typography>
    </div>
  )
}

const StyledTag = styled(Tag)<TagProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.neutral100};
  border-radius: 12px;
  padding: 4px 8px;
  color: ${({ theme }) => theme.palette.neutral900};
  height: 32px;
  overflow: hidden;
  & p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

const ListBox = styled(Popper)`
  background-color: ${({ theme }) => theme.palette.neutral000};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows[4]};
  z-index: 1;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndex.modal + 1};

  ul {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none;
    }
    list-style: none;
    margin: 2px 0;
    padding: 0;
    overflow: auto;
    max-height: 222px;
  }
`

const ListItem = styled('li')`
  padding: 2px 8px;
  display: flex;
  min-height: 40px;
  .select__list-item_label {
    width: 100%;
    padding: 6px 8px;
    border-radius: 4px;
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: space-between;
    svg {
      width: 20px;
      height: 20px;
      color: transparent;
    }
  }

  &[aria-selected='true'] {
    .select__list-item_label {
      background-color: ${({ theme }) => theme.palette.primary50};
      svg {
        color: ${({ theme }) => theme.palette.primary500};
      }
    }
    &.${autocompleteClasses.focused} {
      .select__list-item_label {
        //background-color: transparent;
      }
    }
  }

  &.${autocompleteClasses.focused} {
    background-color: ${({ theme }) => alpha(theme.palette.neutral1000, 0.07)};
    cursor: pointer;
  }
`

interface Props<T> {
  searchable?: boolean
  multiple?: boolean
  placeholder?: string
  id?: string
  label?: string
  options: T[]
  value: T | T[] | null
  onChange: (value: T | T[] | null) => void
  getOptionLabel: (option: T) => string
  error?: boolean
  helperText?: ReactNode
  fullWidth?: boolean
  className?: string
  disabled?: boolean
  loading?: boolean
  emptyText?: string
  loadingText?: string
  required?: boolean
}

function SelectWithChipsComponent<T>(
  {
    id,
    label,
    options,
    value,
    onChange,
    getOptionLabel,
    helperText,
    searchable = false,
    multiple = false,
    placeholder = '',
    error = false,
    fullWidth = true,
    className = '',
    disabled = false,
    loading = false,
    required = false,
    emptyText = t('selectMenu.nothingFound'),
    loadingText = t('selectMenu.loading'),
  }: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const [open, setOpen] = useState(false)

  const {
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    anchorEl,
    setAnchorEl,
  } = useAutocomplete({
    id: id || 'select-with-chips',
    multiple,
    options,
    getOptionLabel,
    disableCloseOnSelect: multiple,
    onOpen() {
      if (searchable) setOpen(true)
    },
    open,
    onClose() {
      setOpen(false)
    },
    value,
    onChange: (_, val) => {
      if (Array.isArray(val)) {
        onChange(sortArrayByArray(val, options))
        return
      }
      onChange(val)
    },
    disabled,
  })

  const inputWrapperRef = useForkRef(ref, setAnchorEl)
  const rootRef = useRef<HTMLDivElement>(null)

  const calculatedPlaceholder = !(Array.isArray(value) && value.length)
    ? placeholder
    : ''

  useHistoryCustomBack(() => setOpen(false), [], open)

  const handleExpandIconClick = useCallback(() => {
    if (searchable && rootRef.current) {
      const input = rootRef.current.querySelector(
        '.select-input',
      ) as HTMLInputElement | null
      input?.focus()
    }
    setOpen(state => !state)
  }, [])

  const preventDefault = useCallback((e: any) => {
    e.preventDefault()
  }, [])

  const inputHidden = useMemo(() => {
    if (!searchable && Array.isArray(value) && value.length > 0) return true
    return false
  }, [value, searchable])

  return (
    <Root className={className} ref={rootRef} data-is-full-width={fullWidth}>
      {label && (
        <Label
          {...getInputLabelProps()}
          onClick={() => !searchable && setOpen(true)}
        >
          {label}
          {required && (
            <Typography
              variant="subtitle3"
              sx={{ marginRight: '4px' }}
              color="error500"
            >
              *
            </Typography>
          )}
        </Label>
      )}

      <InputWrapper
        ref={inputWrapperRef}
        className={`${focused ? 'focused' : ''} ${error ? 'error' : ''}`}
        data-disabled={disabled}
      >
        {Array.isArray(value) &&
          value.map((option: T, index: number) => {
            return (
              <StyledTag
                label={getOptionLabel(option)}
                {...getTagProps({ index })}
              />
            )
          })}

        <input
          className={`select-input ${inputHidden && 'hidden'}`}
          {...getInputProps()}
          data-focusable={searchable}
          readOnly={!searchable}
          placeholder={calculatedPlaceholder}
        />
        <IconButton
          disabled={disabled}
          size="small"
          className="expand-icon"
          onClick={handleExpandIconClick}
          onMouseDown={preventDefault}
          onTouchStart={preventDefault}
        >
          {loading ? (
            <CircularLoading size={17} color="inherit" />
          ) : (
            <ExpandMoreIcon data-is-open={open} />
          )}
        </IconButton>
        {!searchable && (
          <label
            className="overlay"
            onClick={() => setOpen(open => !open)}
            {...getInputLabelProps()}
            onMouseDown={preventDefault}
            onTouchStart={preventDefault}
          />
        )}
      </InputWrapper>
      {helperText && (
        <FormHelperText
          sx={{
            marginTop: '8px',
            marginRight: '8px',
            color: error ? 'error500' : undefined,
            display: 'flex',
            alignItems: 'center',
            '& > svg': {
              width: '20px',
              height: '20px',
              marginLeft: '4px',
            },
          }}
        >
          {helperText}
        </FormHelperText>
      )}
      {anchorEl && (
        <ListBox
          sx={{ width: anchorEl?.clientWidth }}
          open={open}
          anchorEl={anchorEl}
        >
          {groupedOptions?.length > 0 ? (
            <ul {...getListboxProps()}>
              {(groupedOptions as T[]).map((option, index) => (
                <ListItem {...getOptionProps({ option, index })}>
                  <Typography
                    className="select__list-item_label"
                    variant="body2"
                  >
                    {getOptionLabel(option)} <WCheckIcon />
                  </Typography>
                </ListItem>
              ))}
            </ul>
          ) : loading ? (
            <ListItem>
              <Typography
                className="select__list-item_label placeholder"
                variant="subtitle2"
                color="neutral500"
              >
                {loadingText}...
              </Typography>
            </ListItem>
          ) : (
            <ListItem>
              <Typography
                className="select__list-item_label placeholder"
                variant="body2"
              >
                {emptyText}
              </Typography>
            </ListItem>
          )}
        </ListBox>
      )}
    </Root>
  )
}

export const SelectWithChips = forwardRef(SelectWithChipsComponent) as <T>(
  props: Props<T> & { ref?: ForwardedRef<HTMLUListElement> },
) => ReturnType<typeof SelectWithChipsComponent>
